import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Divider,
  Flex,
  Form,
  InputNumber,
  message,
  Row,
  Space,
  Spin,
  Tag,
  TimePicker,
  Tooltip,
  Typography,
} from 'antd';
import {
  BarChartOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  MoonOutlined,
  PlusOutlined,
  SaveOutlined,
  SunOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { currency, useIsEditAllowedWithOperator } from '../../utils/utils';
import { useFetchTouPricingDetail, useUpsertTouPricing } from './Hooks';
import {
  analyzeCoverage,
  buildDefaultPeriods,
  buildPeriodsFromTariff,
  classifyTouSlotKind,
  dayjsToTime,
  daysInMonthFor,
  findNextDayGap,
  findNextTimeGap,
  makeDayValidators,
  makePeriodsListValidator,
  makeRateValidator,
  makeSlotsListValidator,
  makeTimeRangeValidator,
  mapFormPeriods,
  minutesToTime,
  timeToDayjs,
} from './touFormUtils';
import { formatReportNumber } from '../../utils/CommonFunctions';

const { Text } = Typography;

const SLOT_KIND_LABEL = {
  day: 'touSlotDay',
  evening: 'touSlotEvening',
  night: 'touSlotNight',
  period: 'touSlotPeriod',
};

const formatSlotLabel = (slot, tReport) => {
  const start = dayjsToTime(slot?.timeRange?.[0], false);
  const end = dayjsToTime(slot?.timeRange?.[1], true);
  if (!start || !end) {
    return {
      label: tReport('touSlotPeriod'),
      start: '—',
      end: '—',
      icon: <SunOutlined />,
    };
  }

  const kind = classifyTouSlotKind(start, end);
  return {
    label: tReport(SLOT_KIND_LABEL[kind] || SLOT_KIND_LABEL.period),
    start,
    end,
    icon: kind === 'day' ? <SunOutlined /> : <MoonOutlined />,
  };
};

/**
 * Compact TOU editor for Create Report — table + live rate schedule summary.
 * @param {boolean} [ephemeral] when true, edits are for report generation only (no DB save)
 * @param {function} [onPeriodsChange] called with API-shaped periods when form changes (ephemeral)
 */
const TouInlineEditor = ({
  operatorId,
  year: yearProp,
  month: monthProp,
  factoryId = 'fallback',
  onCoverageChange,
  onToolbarChange,
  onSaved,
  ephemeral = false,
  onPeriodsChange,
  extraActions = null,
}) => {
  const { t } = useTranslation('touPricing');
  const { t: tReport } = useTranslation('reportPage');
  const isEditAllowed = useIsEditAllowedWithOperator();
  const [form] = Form.useForm();
  const watchedPeriods = Form.useWatch('periods', form);
  const [dirty, setDirty] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const hydratedKeyRef = useRef('');
  const saveTokenRef = useRef(0);
  const dirtyRef = useRef(false);
  const onCoverageChangeRef = useRef(onCoverageChange);
  const onToolbarChangeRef = useRef(onToolbarChange);
  const onSavedRef = useRef(onSaved);
  const onPeriodsChangeRef = useRef(onPeriodsChange);
  const persistRef = useRef(async () => false);

  dirtyRef.current = dirty;
  onCoverageChangeRef.current = onCoverageChange;
  onToolbarChangeRef.current = onToolbarChange;
  onSavedRef.current = onSaved;
  onPeriodsChangeRef.current = onPeriodsChange;

  const fallback = dayjs().subtract(1, 'month');
  const year = yearProp != null ? Number(yearProp) : fallback.year();
  const month = monthProp != null ? Number(monthProp) : fallback.month() + 1;
  const daysInMonth = daysInMonthFor(year, month);
  const monthKey = `${operatorId || ''}-${year}-${month}-${factoryId || 'fallback'}`;
  const monthShort = dayjs().year(year).month(month - 1).format('MMM');

  const { data, isLoading, isFetching, isError, error } = useFetchTouPricingDetail(
    operatorId,
    year,
    month,
    !!operatorId && Number.isFinite(year) && Number.isFinite(month),
    factoryId,
  );
  const upsertMutation = useUpsertTouPricing();

  const coverage = useMemo(
    () => analyzeCoverage(watchedPeriods, daysInMonth, t),
    [watchedPeriods, daysInMonth, t],
  );
  const coverageIssue = coverage.issues?.[0] || '';

  const datedPeriods = useMemo(
    () =>
      (watchedPeriods || []).filter(
        (period) => period?.dateFrom != null && period?.dateTo != null,
      ),
    [watchedPeriods],
  );

  const summaryItems = useMemo(() => {
    const items = [];
    (watchedPeriods || []).forEach((period, pi) => {
      const from = period?.dateFrom;
      const to = period?.dateTo;
      const rangeLabel =
        from != null && to != null ? `${from} – ${to} ${monthShort}` : t('periodDaysUnset');
      (period?.slots || []).forEach((slot, si) => {
        const meta = formatSlotLabel(slot, tReport);
        const rate =
          slot?.rate === undefined || slot?.rate === null || slot?.rate === ''
            ? null
            : Number(slot.rate);
        items.push({
          key: `${pi}-${si}`,
          periodKey: pi,
          rangeLabel,
          ...meta,
          rate,
        });
      });
    });
    return items;
  }, [watchedPeriods, monthShort, t, tReport]);

  const summaryRange = useMemo(() => {
    if (!datedPeriods.length) return tReport('touRateSchedule');
    const rangeText = datedPeriods
      .map((period) => `${period.dateFrom} – ${period.dateTo}`)
      .join(', ');
    return tReport('touSummaryFor', {
      range: `${rangeText} ${monthShort}`,
    });
  }, [datedPeriods, monthShort, tReport]);

  const showRangeHeaders = datedPeriods.length > 1;

  useEffect(() => {
    hydratedKeyRef.current = '';
    setHydrated(false);
    setDirty(false);
    dirtyRef.current = false;
    form.resetFields();
  }, [monthKey, form]);

  useEffect(() => {
    if (!operatorId || isLoading) return;
    const tariffId = data?.tariff?._id || data?.tariff?.id || 'new';
    const revision = data?.tariff?.updated_at || '';
    const key = `${monthKey}-${tariffId}-${revision}`;
    if (hydratedKeyRef.current === key) return;
    // Same key means this save is already on the form, so unsaved edits stay.
    // A copy keeps the tariff id and changes updated_at, which loads the new rates.

    hydratedKeyRef.current = key;
    if (data?.tariff) {
      form.setFieldsValue({ periods: buildPeriodsFromTariff(data.tariff) });
    } else {
      form.setFieldsValue({ periods: buildDefaultPeriods(year, month) });
    }
    setDirty(false);
    dirtyRef.current = false;
    setHydrated(true);
  }, [operatorId, isLoading, data, monthKey, year, month, form]);

  useEffect(() => {
    const loading = !operatorId || isLoading || !hydrated;
    // Ephemeral: dirty edits are intended for the report — coverage alone gates generate
    const completeOk =
      !loading &&
      !!coverage.isComplete &&
      (ephemeral || !dirty);
    onCoverageChangeRef.current?.({
      isComplete: completeOk,
      status: coverage.status,
      isLoading: loading,
      isSaving: ephemeral ? false : upsertMutation.isPending,
      dirty,
    });
  }, [
    coverage.isComplete,
    coverage.status,
    dirty,
    ephemeral,
    hydrated,
    isLoading,
    operatorId,
    upsertMutation.isPending,
  ]);

  useEffect(() => {
    if (!ephemeral || !hydrated) return;
    const mapped = mapFormPeriods({ periods: watchedPeriods || [] });
    onPeriodsChangeRef.current?.(mapped);
  }, [ephemeral, hydrated, watchedPeriods]);

  const persist = async () => {
    try {
      const values = await form.validateFields();
      const live = analyzeCoverage(values.periods, daysInMonth, t);
      if (!live.isComplete) {
        message.error(live.issues[0] || t('coverageGaps'));
        return false;
      }
      const token = ++saveTokenRef.current;
      await upsertMutation.mutateAsync({
        operatorId,
        year,
        month,
        factoryId: factoryId === 'fallback' ? 'fallback' : factoryId,
        periods: mapFormPeriods(values),
        status: 'ACTIVE',
      });
      if (token !== saveTokenRef.current) return false;
      setDirty(false);
      dirtyRef.current = false;
      message.success(t('saveSuccess'));
      onSavedRef.current?.();
      return true;
    } catch (err) {
      if (err?.errorFields) {
        message.error(err.errorFields[0]?.errors?.[0] || t('validation.formInvalid'));
        return false;
      }
      message.error(err?.response?.data?.message || err?.message || 'Error');
      return false;
    }
  };
  persistRef.current = persist;

  const markDirty = () => {
    dirtyRef.current = true;
    setDirty(true);
  };

  const revalidatePeriodDays = () => {
    const periods = form.getFieldValue('periods') || [];
    const names = [];
    periods.forEach((_, i) => {
      names.push(['periods', i, 'dateFrom'], ['periods', i, 'dateTo']);
    });
    if (names.length) form.validateFields(names).catch(() => {});
  };

  const revalidatePeriodSlots = (periodIndex) => {
    // Re-check times + day coverage only. Rate fields validate themselves on change/blur
    // so adding a new empty slot does not paint the whole row red immediately.
    const slots = form.getFieldValue(['periods', periodIndex, 'slots']) || [];
    const names = [['periods', periodIndex, 'slots']];
    slots.forEach((_, j) => {
      names.push(['periods', periodIndex, 'slots', j, 'timeRange']);
    });
    form.validateFields(names).catch(() => {});
  };

  const showToolbar =
    !!operatorId && !isLoading && hydrated && !isError && !!onToolbarChange;

  // Push toolbar to parent header — never clear in effect cleanup (that caused update loops)
  useEffect(() => {
    const pushToolbar = onToolbarChangeRef.current;
    if (!pushToolbar) return undefined;

    if (!showToolbar) {
      pushToolbar(null);
      return undefined;
    }

    const statusTag = upsertMutation.isPending && !ephemeral ? (
      <Tag color="processing" className="m-0!">{tReport('touSaving')}</Tag>
    ) : !ephemeral && dirty ? (
      <Tag color="gold" className="m-0!">{tReport('touUnsaved')}</Tag>
    ) : coverage.status === 'complete' ? (
      <Tag color="success" className="m-0!">{t('complete')}</Tag>
    ) : coverage.status === 'incomplete' ? (
      <Tag color="gold" className="m-0!">{t('incomplete')}</Tag>
    ) : (
      <Tag color="error" className="m-0!">{t('hasGaps')}</Tag>
    );

    pushToolbar(
      <>
        {statusTag}
        {!ephemeral && isEditAllowed && (
          <Tooltip title={coverage.status !== 'complete' ? coverageIssue : undefined}>
            <span>
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                loading={upsertMutation.isPending}
                disabled={!dirty || coverage.status !== 'complete'}
                onClick={() => persistRef.current()}
              >
                {t('save')}
              </Button>
            </span>
          </Tooltip>
        )}
      </>,
    );

    return undefined;
  }, [
    showToolbar,
    coverage.status,
    coverageIssue,
    dirty,
    ephemeral,
    isEditAllowed,
    upsertMutation.isPending,
    t,
    tReport,
  ]);

  useEffect(() => {
    return () => {
      onToolbarChangeRef.current?.(null);
    };
  }, []);

  if (!operatorId) {
    return <Text type="secondary">{tReport('selectOperatorFirst')}</Text>;
  }

  if (isLoading || !hydrated) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50/70 py-10">
        <Spin spinning tip={tReport('loadingTouRates')}>
          <div className="min-h-[140px]" />
        </Spin>
      </div>
    );
  }

  if (isError) {
    return (
      <Alert
        type="error"
        showIcon
        message={error?.response?.data?.message || error?.message || t('loadError')}
      />
    );
  }

  return (
    <Spin spinning={isFetching && !dirty} tip={tReport('loadingTouRates')}>
      <style>{`
        .tou-report-editor .ant-form-item { margin-bottom: 0; }
        .tou-report-editor .ant-form-item-explain-connected { min-height: 0; }
        .tou-report-editor .ant-form-item-explain {
          font-size: 11px;
          line-height: 1.25;
          max-width: 100%;
        }
        .tou-report-table {
          border: 1px solid #f0f0f0;
          border-radius: 8px;
          overflow: visible;
          background: #fff;
        }
        .tou-report-table-head {
          display: grid;
          grid-template-columns: 112px minmax(0, 1fr) minmax(152px, 0.7fr) 44px;
          gap: 8px;
          padding: 8px 16px;
          background: #fafafa;
          border-bottom: 1px solid #f0f0f0;
          border-radius: 8px 8px 0 0;
          font-size: 12px;
          color: rgba(0,0,0,0.45);
          font-weight: 500;
        }
        .tou-report-period + .tou-report-period {
          border-top: 1px solid #f0f0f0;
        }
        .tou-report-period-grid {
          display: grid;
          grid-template-columns: 112px minmax(0, 1fr);
          gap: 8px;
          padding: 10px 16px 4px;
          align-items: start;
        }
        .tou-report-date-col {
          padding-top: 4px;
        }
        .tou-report-slot-row {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(152px, 0.7fr) 44px;
          gap: 8px;
          align-items: start;
          padding: 4px 0;
        }
        .tou-report-slot-row > * {
          min-width: 0;
        }
        .tou-report-slot-row + .tou-report-slot-row {
          border-top: 1px dashed #f5f5f5;
          margin-top: 4px;
          padding-top: 8px;
        }
        .tou-report-slot-row .ant-picker {
          width: 100%;
          min-width: 0;
          max-width: 100%;
        }
        .tou-report-slot-row .ant-picker-range {
          overflow: hidden;
        }
        .tou-report-slot-row .ant-picker-input {
          min-width: 0;
          overflow: hidden;
        }
        .tou-report-slot-row .ant-picker-input > input {
          font-variant-numeric: tabular-nums;
        }
        .tou-report-slot-row .ant-input-number,
        .tou-report-slot-row .ant-input-number-group-wrapper,
        .tou-report-slot-row .ant-input-number-group {
          width: 100%;
          min-width: 0;
          max-width: 100%;
        }
        .tou-report-slot-row > .tou-report-slot-delete {
          display: flex;
          align-items: flex-start;
          justify-content: center;
          width: 44px;
          min-width: 44px;
          padding-top: 2px;
          overflow: visible;
        }
        .tou-report-slot-delete .ant-btn {
          width: 32px;
          height: 32px;
          padding: 0;
        }
        .tou-report-period-actions {
          padding: 4px 0 10px;
        }
        .tou-report-slot-errors {
          padding: 0 0 4px;
        }
        /* Above Drawer (1000) so the panel is never clipped by drawer/table overflow */
        .tou-time-picker-dropdown.ant-picker-dropdown {
          z-index: 2200 !important;
        }
        @media (max-width: 700px) {
          .tou-report-table-head { display: none; }
          .tou-report-period-grid {
            grid-template-columns: 1fr;
          }
          .tou-report-slot-row {
            grid-template-columns: 1fr;
            gap: 8px;
          }
          .tou-report-slot-row .ant-picker {
            min-width: 0;
          }
        }
      `}</style>

      {coverage.status === 'gaps' && coverageIssue && (
        <Alert
          type="error"
          showIcon
          className="mb-3!"
          message={<span className="text-xs">{coverageIssue}</span>}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={24}>
          <Form
            form={form}
            layout="vertical"
            size="small"
            disabled={!isEditAllowed}
            requiredMark={false}
            validateTrigger={['onChange', 'onBlur']}
            onValuesChange={() => markDirty()}
            className="tou-report-editor"
          >
            <Form.List
              name="periods"
              rules={[{ validator: makePeriodsListValidator(daysInMonth, t) }]}
            >
              {(fields, { add, remove }, { errors: periodListErrors }) => (
                <Space orientation="vertical" size={12} className="w-full">
                  <div className="tou-report-table">
                    <div className="tou-report-table-head">
                      <span>{tReport('touColDateRange')}</span>
                      <span>{t('timeRange')}</span>
                      <span>{t('rate')}</span>
                      <span />
                    </div>

                    {fields.map(({ key, name }) => {
                      const dayValidators = makeDayValidators(form, name, daysInMonth, t);
                      return (
                        <div key={key} className="tou-report-period">
                          <div className="tou-report-period-grid">
                            <div className="tou-report-date-col">
                              <div className="flex flex-col gap-1">
                                <Flex align="center" gap={4} wrap>
                                  <Form.Item
                                    name={[name, 'dateFrom']}
                                    rules={[{ validator: dayValidators.dateFrom }]}
                                  >
                                    <InputNumber
                                      min={1}
                                      max={daysInMonth}
                                      precision={0}
                                      size="small"
                                      className="w-14!"
                                      onChange={() =>
                                        queueMicrotask(revalidatePeriodDays)
                                      }
                                    />
                                  </Form.Item>
                                  <Text type="secondary">–</Text>
                                  <Form.Item
                                    name={[name, 'dateTo']}
                                    dependencies={[['periods', name, 'dateFrom']]}
                                    rules={[{ validator: dayValidators.dateTo }]}
                                  >
                                    <InputNumber
                                      min={1}
                                      max={daysInMonth}
                                      precision={0}
                                      size="small"
                                      className="w-14!"
                                      onChange={() =>
                                        queueMicrotask(revalidatePeriodDays)
                                      }
                                    />
                                  </Form.Item>
                                </Flex>
                                <Text type="secondary" className="text-xs">
                                  {monthShort}
                                </Text>
                                {fields.length > 1 && (
                                  <Tooltip title={t('removePeriod')}>
                                    <Button
                                      danger
                                      type="text"
                                      size="small"
                                      icon={<DeleteOutlined />}
                                      className="self-start px-0!"
                                      onClick={() => {
                                        remove(name);
                                        markDirty();
                                        queueMicrotask(revalidatePeriodDays);
                                      }}
                                      disabled={!isEditAllowed}
                                    />
                                  </Tooltip>
                                )}
                              </div>
                            </div>

                            <div className="tou-report-slots-col">
                              <Form.List
                                name={[name, 'slots']}
                                rules={[{ validator: makeSlotsListValidator(t) }]}
                              >
                                {(slotFields, slotOps, { errors: slotListErrors }) => (
                                  <>
                                    {slotFields.map((sf) => (
                                      <div key={sf.key} className="tou-report-slot-row">
                                        <Form.Item
                                          name={[sf.name, 'timeRange']}
                                          rules={[
                                            {
                                              validator: makeTimeRangeValidator(
                                                form,
                                                name,
                                                sf.name,
                                                t,
                                              ),
                                            },
                                          ]}
                                        >
                                          <TimePicker.RangePicker
                                            format="HH:mm"
                                            size="small"
                                            className="w-full"
                                            order={false}
                                            needConfirm={false}
                                            placement="bottomLeft"
                                            getPopupContainer={() => document.body}
                                            classNames={{ popup: { root: 'tou-time-picker-dropdown' } }}
                                            styles={{ popup: { root: { zIndex: 2200 } } }}
                                            placeholder={[t('startTime'), t('endTime')]}
                                            onChange={() =>
                                              queueMicrotask(() =>
                                                revalidatePeriodSlots(name),
                                              )
                                            }
                                          />
                                        </Form.Item>

                                        <Form.Item
                                          name={[sf.name, 'rate']}
                                          rules={[{ validator: makeRateValidator(t) }]}
                                          validateTrigger={['onBlur', 'onChange']}
                                        >
                                          <InputNumber
                                            min={0}
                                            step={0.000001}
                                            precision={6}
                                            decimalSeparator=","
                                            size="small"
                                            className="w-full"
                                            placeholder=""
                                            addonAfter={currency}
                                            onChange={() =>
                                              queueMicrotask(() =>
                                                revalidatePeriodSlots(name),
                                              )
                                            }
                                          />
                                        </Form.Item>

                                        <div className="tou-report-slot-delete">
                                          {slotFields.length > 1 ? (
                                            <Tooltip title={t('removeSlot')}>
                                              <Button
                                                danger
                                                type="text"
                                                size="small"
                                                icon={<DeleteOutlined />}
                                                aria-label={t('removeSlot')}
                                                onClick={() => {
                                                  slotOps.remove(sf.name);
                                                  markDirty();
                                                  queueMicrotask(() =>
                                                    revalidatePeriodSlots(name),
                                                  );
                                                }}
                                                disabled={!isEditAllowed}
                                              />
                                            </Tooltip>
                                          ) : (
                                            <span className="inline-block w-8" />
                                          )}
                                        </div>
                                      </div>
                                    ))}

                                    {slotListErrors?.length > 0 && (
                                      <div className="tou-report-slot-errors">
                                        <Form.ErrorList errors={slotListErrors} />
                                      </div>
                                    )}

                                    <div className="tou-report-period-actions">
                                      <Button
                                        type="link"
                                        size="small"
                                        icon={<PlusOutlined />}
                                        className="px-0!"
                                        onClick={() => {
                                          const current = form.getFieldValue([
                                            'periods',
                                            name,
                                          ]);
                                          const gap = findNextTimeGap(current?.slots);
                                          if (!gap) {
                                            message.warning(
                                              t('validation.shortenBeforeAdd'),
                                            );
                                            return;
                                          }
                                          slotOps.add({
                                            timeRange: [
                                              timeToDayjs(minutesToTime(gap.start)),
                                              timeToDayjs(minutesToTime(gap.end)),
                                            ],
                                            rate: undefined,
                                          });
                                          markDirty();
                                          queueMicrotask(() =>
                                            revalidatePeriodSlots(name),
                                          );
                                        }}
                                      >
                                        {tReport('touAddTimePeriod')}
                                      </Button>
                                    </div>
                                  </>
                                )}
                              </Form.List>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <Form.ErrorList errors={periodListErrors} />

                  <Space>
                  <Button
                    variant="outlined"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      const periods = form.getFieldValue('periods') || [];
                      const gap = findNextDayGap(periods, daysInMonth);
                      if (!gap) {
                        message.warning(t('validation.monthAlreadyCovered'));
                        return;
                      }
                      add({
                        dateFrom: gap.dateFrom,
                        dateTo: gap.dateTo,
                        slots: [
                          {
                            timeRange: [
                              dayjs('00:00', 'HH:mm'),
                              dayjs('23:59', 'HH:mm'),
                            ],
                            rate: undefined,
                          },
                        ],
                      });
                      markDirty();
                      queueMicrotask(revalidatePeriodDays);
                    }}
                  >
                    {tReport('touAddDateRange')}
                  </Button>
                  {extraActions}
                  </Space>
                </Space>
              )}
            </Form.List>
          </Form>
        </Col>

        <Col xs={24} lg={24}>
          <div className="h-full rounded-lg border border-gray-200 bg-white p-4">
            <Flex align="center" gap={8} className="mb-1!">
              <BarChartOutlined className="text-gray-400" />
              <Text strong>{tReport('touRateSchedule')}</Text>
            </Flex>
            <Text type="secondary" className="mb-4! block text-xs">
              {summaryRange}
            </Text>

            <Space orientation="vertical" size={12} className="w-full">
              {summaryItems.length === 0 && (
                <Text type="secondary" className="text-xs">
                  {tReport('touSummaryEmpty')}
                </Text>
              )}
              {summaryItems.map((item, idx) => (
                <div key={item.key}>
                  {showRangeHeaders &&
                    item.periodKey !== summaryItems[idx - 1]?.periodKey && (
                      <Text type="secondary" className="mb-1! block text-xs font-medium">
                        {item.rangeLabel}
                      </Text>
                    )}
                  <Flex justify="space-between" align="center" gap={8}>
                    <Flex gap={8} align="center">
                      <span className="text-base text-gray-400">{item.icon}</span>
                      <div className="text-sm text-gray-800">
                        <span className="font-medium">{item.label}</span>
                        <span className="text-gray-500">
                          {' '}
                          ({item.start} – {item.end})
                        </span>
                      </div>
                    </Flex>
                    <Text strong className="whitespace-nowrap text-sm">
                      {item.rate == null || Number.isNaN(item.rate)
                        ? '—'
                        : `${formatReportNumber(item.rate)} ${currency}/kWh`}
                    </Text>
                  </Flex>
                </div>
              ))}
            </Space>

            <Divider className="my-4!" />
            <Flex gap={8} align="flex-start">
              <InfoCircleOutlined className="mt-0.5 text-gray-400" />
              <Text type="secondary" className="text-xs leading-snug">
                {tReport('touRatesHelp')}
              </Text>
            </Flex>
          </div>
        </Col>
      </Row>
    </Spin>
  );
};

export default TouInlineEditor;
